export default function trustedInlinePlugin(normalizeToolName) {
  return (tree) => {
    const re = /\[\s*([a-zA-Z_<>]+)\s*\^\s*task_id:([a-zA-Z0-9-]{8,})(?:\s*\|\s*path:([\s\S]*?))(?:\s*\|\s*excerpt:"([\s\S]*?)")?\s*\]/g;
    const DEBUG = false;
    const processChildren = (children) => {
      if (!Array.isArray(children)) return children;
      const out = [];
      for (let i = 0; i < children.length; i++) {
        const node = children[i];
        if (node && typeof node === 'object' && Array.isArray(node.children)) {
          node.children = processChildren(node.children);
          out.push(node);
          continue;
        }
        if (node && node.type === 'text' && typeof node.value === 'string') {
          const text = node.value;
          const found = [];
          let m;
          while ((m = re.exec(text)) !== null) {
            found.push({
              raw: m[0],
              tool: normalizeToolName(m[1] || ''),
              taskId: (m[2] || '').trim(),
              path: (m[3] || '').trim(),
              excerpt: (m[4] || '').trim(),
              start: m.index,
              end: m.index + m[0].length
            });
          }
          if (DEBUG && found.length > 0) {
            try {
              for (const r of found) {
                console.log('[trustedInline] bracketRef ok', {
                  tool: r.tool,
                  taskId: r.taskId,
                  path: r.path,
                  excerpt: r.excerpt,
                  start: r.start,
                  end: r.end,
                  raw: r.raw
                });
              }
            } catch (_) {
              /* ignore */
            }
          }
          const jsonRefs = (function () {
            const refs = [];
            let idx = 0;
            while (idx < text.length) {
              const s = text.indexOf('{', idx);
              if (s === -1) break;
              let j = s + 1;
              let depth = 1;
              let inQ = false;
              while (j < text.length && depth > 0) {
                const ch = text[j];
                if (ch === '"') {
                  // Determine if this quote is escaped by an odd number of backslashes immediately preceding it
                  let bs = 0;
                  let k = j - 1;
                  while (k >= s && text[k] === '\\') {
                    bs += 1;
                    k -= 1;
                  }
                  if (bs % 2 === 0) inQ = !inQ; // only toggle when not escaped
                }
                if (!inQ) {
                  if (ch === '{') depth++;
                  else if (ch === '}') depth--;
                }
                j++;
              }
              if (depth === 0) {
                const raw = text.slice(s, j);
                try {
                  const obj = JSON.parse(raw);
                  const tool = normalizeToolName(obj.report_type || obj.tool || '');
                  const taskId = obj.task_id || '';
                  const path = obj.path || '';
                  const excerpt = typeof obj.excerpt === 'string' ? obj.excerpt : '';
                  if (tool && taskId) {
                    refs.push({ raw, tool, taskId, path, excerpt, start: s, end: j });
                    if (DEBUG) {
                      try {
                        console.log('[trustedInline] jsonRef ok', { tool, taskId, path, excerpt, start: s, end: j, raw });
                      } catch (_) {
                        /* ignore */
                      }
                    }
                  }
                } catch (e) {
                  // Fallback tolerant extractor for invalid JSON (e.g., unescaped quotes inside path[] keys)
                  try {
                    const toolMatch = raw.match(/"(?:report_type|tool)"\s*:\s*"([^"]+)"/);
                    const taskIdMatch = raw.match(/"task_id"\s*:\s*"([^"]+)"/);
                    const pathMatch = raw.match(/"path"\s*:\s*"([\s\S]*?)"(?=\s*,\s*"(?:excerpt|task_id|report_type|tool)"|\s*})/);
                    const excerptMatch = raw.match(/"excerpt"\s*:\s*"([\s\S]*?)"(?=\s*[},])/);
                    const tool = normalizeToolName((toolMatch && toolMatch[1]) || '');
                    const taskId = (taskIdMatch && taskIdMatch[1]) || '';
                    const path = (pathMatch && pathMatch[1]) || '';
                    const excerpt = (excerptMatch && excerptMatch[1]) || '';
                    if (tool && taskId) {
                      refs.push({ raw, tool, taskId, path, excerpt, start: s, end: j });
                      if (DEBUG) {
                        try {
                          console.log('[trustedInline] jsonRef salvaged', { tool, taskId, path, excerpt, start: s, end: j, raw });
                        } catch (_) {
                          /* ignore */
                        }
                      }
                    } else if (DEBUG) {
                      try {
                        const snippet = text.slice(s, Math.min(j, s + 160));
                        console.warn('[trustedInline] jsonRef parse error (unrecoverable)', {
                          start: s,
                          end: j,
                          snippet,
                          error: String(e && e.message ? e.message : e)
                        });
                      } catch (_) {
                        /* ignore */
                      }
                    }
                  } catch (e2) {
                    if (DEBUG) {
                      try {
                        const snippet = text.slice(s, Math.min(j, s + 160));
                        console.warn('[trustedInline] jsonRef parse error (fallback failed)', {
                          start: s,
                          end: j,
                          snippet,
                          error: String(e2 && e2.message ? e2.message : e2)
                        });
                      } catch (_) {
                        /* ignore */
                      }
                    }
                  }
                }
                idx = j;
              } else {
                break;
              }
            }
            return refs;
          })();

          // Merge and sort by appearance to ensure correct in-order replacement
          const refs = [...found, ...jsonRefs].filter((r) => typeof r.start === 'number' && r.start >= 0).sort((a, b) => a.start - b.start);
          // Deduplicate refs that accidentally overlap or repeat
          for (let k = 1; k < refs.length; k++) {
            if (refs[k - 1] && refs[k] && refs[k - 1].start === refs[k].start && refs[k - 1].end === refs[k].end) {
              refs.splice(k, 1);
              k--;
            }
          }
          if (DEBUG) {
            try {
              console.log('[trustedInline] refs merged', { total: refs.length });
            } catch (_) {
              /* ignore */
            }
          }
          if (refs.length === 0) {
            if (DEBUG) {
              try {
                console.log('[trustedInline] no refs in chunk', { text: text.length > 200 ? text.slice(0, 200) + '…' : text });
              } catch (_) {
                /* ignore */
              }
            }
            out.push(node);
          } else {
            let cursor = 0;
            for (const ref of refs) {
              const start = ref.start;
              const end = ref.end;
              if (start < cursor) {
                // Already passed; skip to avoid corrupt slicing
                if (DEBUG) {
                  try {
                    console.warn('[trustedInline] skip overlapped ref', { start, end, cursor, raw: text.slice(start, end) });
                  } catch (_) {
                    /* ignore */
                  }
                }
                continue;
              }
              if (start > cursor) out.push({ type: 'text', value: text.slice(cursor, start) });
              const q =
                ref.path || ref.excerpt
                  ? `?path=${encodeURIComponent(ref.path || '')}&excerpt=${encodeURIComponent(ref.excerpt || '')}`
                  : '';
              const url = `trusted:${ref.tool}:${ref.taskId}${q}`;
              out.push({ type: 'link', url, children: [{ type: 'text', value: 'TrustedReport' }] });
              if (DEBUG) {
                try {
                  console.log('[trustedInline] link emitted', { url, start, end });
                } catch (_) {
                  /* ignore */
                }
              }
              cursor = end;
            }
            if (cursor < text.length) out.push({ type: 'text', value: text.slice(cursor) });
          }
          continue;
        }
        out.push(node);
      }
      return out;
    };
    if (tree && Array.isArray(tree.children)) {
      tree.children = processChildren(tree.children);
    }
  };
}
